import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';
import { useTheme } from '../context/ThemeContext';
import { useTabletMode } from '../context/TabletModeContext';
import { useTable } from './TableMenuWrapper';
import { 
  trackCategoryOpened, trackProductOpened, trackAddToSelection
} from '../lib/analytics';
import { ArrowLeft, MapPin } from 'lucide-react';
import { ProductPopup } from './ProductPopup';
import UpsellPopup from './UpsellPopup';
import ProductCarousel from './ProductCarousel';
import ProductCard from './ProductCard';
import AIImageDisclaimer from './AIImageDisclaimer';
import GuidedWizard from './GuidedWizard';
import ExperienceDetailPopup from './ExperienceDetailPopup';
import UpsellAddPopup from './UpsellAddPopup';
import CrossSellStrip from './CrossSellStrip';
import { MomentoBanner } from './ModoMomento';
import { SubHeader, subHeaderSpacerHeight } from './SubHeader';
import { useCategoryData, categoryImages, getProductImage, prefetchCategories } from '../hooks/useCategoryData';

export { prefetchCategories };

const experienceNames = {
  bar: { es: 'Bar', en: 'Bar', cat: 'Bar', fr: 'Bar' },
  cafe: { es: 'Cafetería', en: 'Cafeteria', cat: 'Cafeteria', fr: 'Cafétéria' },
  degustacion: { es: 'Degustación', en: 'Tasting', cat: 'Degustació', fr: 'Dégustation' },
  tienda: { es: 'Tienda', en: 'Shop', cat: 'Botiga', fr: 'Boutique' },
};

export const CategoryView = () => {
  const { experienceId, categoryId } = useParams();
  const navigate = useNavigate();
  const { language, localize, t } = useLanguage();
  const { addItem, lastAdded } = useSelection();
  const { isDark } = useTheme();
  const { isTablet } = useTabletMode();
  const { tableNumber } = useTable();
  
  const [selectedCategory, setSelectedCategory] = useState(categoryId || null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState(categoryId ? 'products' : 'categories');
  
  const {
    categories, products, loading,
    carouselIndex, setCarouselIndex, hasLoadedOnce
  } = useCategoryData(experienceId, selectedCategory);

  // Popup states
  const [upsellData, setUpsellData] = useState(null);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [wizardProduct, setWizardProduct] = useState(null);
  const [upsellAddProduct, setUpsellAddProduct] = useState(null);  // product that triggered the upsell
  const [showDownsell, setShowDownsell] = useState(false);          // show downsell after upsell decline
  const [crossSellProduct, setCrossSellProduct] = useState(null);
  const [bannerExperience, setBannerExperience] = useState(null);

  // Navigate to shop event
  useEffect(() => {
    const handler = () => {
      setViewMode('categories');
      setSelectedCategory(null);
      navigate(`/mesa/${tableNumber}/menu/tienda`, { replace: true });
    };
    window.addEventListener('navigateToShop', handler);
    return () => window.removeEventListener('navigateToShop', handler);
  }, [navigate, tableNumber]);

  // When category changes, switch to products view
  useEffect(() => {
    if (selectedCategory) setViewMode('products');
  }, [selectedCategory]);

  const handleCategoryClick = (catId) => {
    const category = categories.find(c => c.id === catId);
    trackCategoryOpened(catId, category?.name?.[language] || catId);
    setSelectedCategory(catId);
    navigate(catId, { replace: true });
  };

  const handleBack = () => {
    if (categoryId || viewMode === 'products') {
      setViewMode('categories');
      setSelectedCategory(null);
      navigate(`../menu/${experienceId}`, { replace: true });
    } else {
      navigate('..');
    }
  };

  const handleProductClick = useCallback((product, index) => {
    if (!product) return;
    trackProductOpened(product);
    window.dispatchEvent(new CustomEvent('pont10_product_opened'));
    setSelectedProduct({ ...product, index });
  }, []);

  const handleProductNav = (direction) => {
    if (!selectedProduct || !products?.length) return;
    const currentIndex = selectedProduct.index;
    const newIndex = direction === 'next' 
      ? Math.min(currentIndex + 1, products.length - 1)
      : Math.max(currentIndex - 1, 0);
    if (newIndex !== currentIndex) {
      const newProduct = products[newIndex];
      if (newProduct) {
        trackProductOpened(newProduct);
        setSelectedProduct({ ...newProduct, index: newIndex });
      }
    }
  };

  const triggerPostAdd = useCallback((addedProduct) => {
    setCrossSellProduct({...addedProduct, _ts: Date.now()});
    // Trigger upsell al añadir if configured
    if (addedProduct?.upsell_product_id) {
      setUpsellAddProduct(addedProduct);
      setShowDownsell(false);
    }
  }, []);

  const handleQuickAdd = useCallback(async (e, product) => {
    e.stopPropagation();
    const hasOptions = product.options?.length > 0 || product.supplements?.length > 0;
    const needsSelection = product.requires_selection || product.requires_bread_selection || product.requires_option_selection;
    if (hasOptions || needsSelection) {
      setWizardProduct(product);
      return;
    }
    trackAddToSelection(product, 1, []);
    addItem(product, 1, []);
    triggerPostAdd(product);
  }, [addItem, triggerPostAdd]);

  const handleWizardComplete = (product) => {
    setWizardProduct(null);
    triggerPostAdd(product);
  };

  const handleUpsellAccept = () => {
    if (upsellData) { trackAddToSelection(upsellData.upsellProduct, 1); addItem(upsellData.upsellProduct); }
    setUpsellData(null); setPendingProduct(null);
  };

  const handleUpsellDecline = () => {
    if (pendingProduct) { trackAddToSelection(pendingProduct, 1); addItem(pendingProduct); }
    setUpsellData(null); setPendingProduct(null);
  };

  const currentCategory = useMemo(
    () => categories.find(c => c.id === selectedCategory),
    [categories, selectedCategory]
  );

  return (
    <div 
      className="min-h-dvh grain safe-bottom transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#1a1a1a' : 'var(--sand-50)', overflowX: 'clip' }}
      data-testid="category-view-screen"
    >
      {/* Header */}
      <SubHeader
        testid="category-header"
        onBack={handleBack}
        backLabel={t('back')}
        title={viewMode === 'products' && currentCategory ? localize(currentCategory.name) : experienceNames[experienceId]?.[language]}
      />

      <div style={{ height: subHeaderSpacerHeight(isTablet) }} />

      <main className={`${isTablet ? 'max-w-6xl px-8 py-10' : 'max-w-5xl px-4 py-6'} mx-auto`} style={{ overflowX: 'clip' }}>
        {/* Categories View */}
        {viewMode === 'categories' && (
          <div className={hasLoadedOnce ? '' : 'view-fade-in'}>
            <p className="text-center text-sm tracking-wider mb-6 transition-colors duration-300"
              style={{ color: isDark ? '#9ca3af' : 'var(--stone-500)' }}>
              {experienceId === 'tienda' ? (
                <span className="font-serif text-base italic not-uppercase normal-case tracking-normal">
                  {language === 'es' && 'Llévate a casa lo que has vivido aquí.'}
                  {language === 'en' && 'Take home what you\'ve experienced here.'}
                  {language === 'cat' && 'Emporta\'t a casa el que has viscut aquí.'}
                  {language === 'fr' && 'Emportez chez vous ce que vous avez vécu ici.'}
                </span>
              ) : (
                <span className="uppercase">
                  {language === 'es' && 'Explora nuestras categorías'}
                  {language === 'en' && 'Explore our categories'}
                  {language === 'cat' && 'Explora les nostres categories'}
                  {language === 'fr' && 'Explorez nos catégories'}
                </span>
              )}
            </p>
            <MomentoBanner onViewExperience={(exp) => exp && setBannerExperience(exp)} onDismiss={() => {}} />
            {categories.length > 0 && (
              <div className={isTablet ? 'grid grid-cols-2 lg:grid-cols-3 gap-6' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                {categories.filter(cat => cat && cat.id).map((cat, index) => (
                  <button key={cat.id} onClick={() => !cat.coming_soon && handleCategoryClick(cat.id)}
                    className={`category-card ${!hasLoadedOnce ? `animate-fade-up delay-${(index + 1) * 100}` : ''} ${cat.coming_soon ? 'opacity-60 cursor-default' : ''}`}
                    data-testid={`category-card-${cat.id}`} disabled={cat.coming_soon}>
                    <img src={cat.image || categoryImages[cat.id] || categoryImages['cat-picar']}
                      alt={localize(cat?.name) || ''} loading="eager" decoding="async" style={{ opacity: 1 }}
                      className={cat.coming_soon ? 'grayscale' : ''}
                      onError={(e) => { e.target.src = categoryImages[cat.id] || categoryImages['cat-picar']; }} />
                    <div className="category-card-content">
                      <h3 className="category-card-title">{localize(cat?.name) || ''}</h3>
                      {cat.coming_soon && (
                        <span className="mt-2 inline-block px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider"
                          style={{ backgroundColor: 'rgba(183, 140, 60, 0.9)', color: 'white' }}>
                          {language === 'es' && 'Próximamente'}{language === 'en' && 'Coming Soon'}
                          {language === 'cat' && 'Pròximament'}{language === 'fr' && 'Bientôt'}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
                {/* Decorative logo tile fills the empty grid slot when count is odd (tablet 2-col) */}
                {isTablet && categories.filter(cat => cat && cat.id).length % 2 === 1 && (
                  <div aria-hidden="true" className="category-card"
                    style={{ background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}>
                    <img src="/pont10-logo.png" alt=""
                      style={{ width: '70%', height: '70%', objectFit: 'contain', opacity: 0.85 }} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Products View */}
        {viewMode === 'products' && (
          <div className="view-fade-in">
            {/* Specialty Coffee Banner */}
            {currentCategory?.specialty_info && (
              <div className="mb-6 p-4 rounded-xl" data-testid="specialty-coffee-banner"
                style={{
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(183, 140, 60, 0.15) 0%, rgba(212, 175, 55, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(183, 140, 60, 0.08) 0%, rgba(212, 175, 55, 0.05) 100%)',
                  border: isDark ? '1px solid rgba(212, 175, 55, 0.2)' : '1px solid rgba(183, 140, 60, 0.2)'
                }}>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #B78C3C 0%, #D4AF37 100%)' }}>
                    <MapPin size={18} color="#FBF5E8" />
                  </div>
                  <div>
                    {(() => {
                      const infoText = localize(currentCategory.specialty_info) || '';
                      const parts = infoText.split('·').map(s => s.trim());
                      const highlight = parts[0] || '';
                      const rest = parts.slice(1).join(' · ');
                      return (
                        <>
                          <p className="text-sm font-bold mb-0.5" style={{ color: isDark ? '#D4AF37' : '#B78C3C' }}>{highlight}</p>
                          {rest && <p className="text-sm font-medium" style={{ color: isDark ? '#F0E4CE' : 'var(--stone-800)' }}>{rest}</p>}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
            
            {/* Skeleton */}
            {loading && products.length === 0 && (
              <div style={{ padding: '0 16px' }} className="view-fade-in">
                <div style={{ borderRadius: '24px', overflow: 'hidden', background: isDark ? '#1e1e1e' : '#fff', border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(183,140,60,0.1)' }}>
                  <div className="skeleton" style={{ width: '100%', height: '260px' }} />
                  <div style={{ padding: '20px' }}>
                    <div className="skeleton" style={{ height: '16px', width: '40%', marginBottom: '12px', borderRadius: '8px' }} />
                    <div className="skeleton" style={{ height: '24px', width: '70%', marginBottom: '8px', borderRadius: '8px' }} />
                    <div className="skeleton" style={{ height: '14px', width: '90%', marginBottom: '16px', borderRadius: '8px' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="skeleton" style={{ height: '28px', width: '80px', borderRadius: '8px' }} />
                      <div className="skeleton" style={{ height: '48px', width: '120px', borderRadius: '24px' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Products: tablet → 2-col grid, mobile → vertical carousel */}
            {products.length > 0 && (
              <div style={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.25s ease-out', pointerEvents: loading ? 'none' : 'auto' }}>
                {isTablet ? (
                  <div
                    data-testid="products-tablet-grid"
                    className="grid grid-cols-2 lg:grid-cols-3 gap-5"
                    style={{ padding: '0 4px' }}
                  >
                    {products.filter(p => p && p.id).map((p, idx) => (
                      <ProductCard
                        key={p.id}
                        product={p}
                        index={idx}
                        onClick={() => handleProductClick(p, idx)}
                        onQuickAdd={(e) => handleQuickAdd(e, p)}
                        isJustAdded={lastAdded?.id === p.id}
                        getImage={getProductImage}
                        localize={localize}
                        language={language}
                        t={t}
                        isDark={isDark}
                      />
                    ))}
                  </div>
                ) : (
                  <ProductCarousel
                    products={products.filter(p => p && p.id)}
                    activeIndex={carouselIndex} onIndexChange={setCarouselIndex}
                    onQuickAdd={handleQuickAdd} onProductClick={handleProductClick}
                    lastAdded={lastAdded} getImage={getProductImage}
                    localize={localize} language={language} t={t} isDark={isDark}
                  />
                )}
              </div>
            )}

            {/* Empty state */}
            {!loading && products.length === 0 && (
              <div className="text-center py-16" style={{ color: 'var(--stone-500)' }} data-testid="no-products-message">
                <p className="font-serif text-xl mb-2">{t('noResults')}</p>
                <p className="text-sm">
                  {language === 'es' && 'Esta categoría se está preparando'}
                  {language === 'en' && 'This category is being prepared'}
                  {language === 'cat' && 'Aquesta categoria s\'està preparant'}
                  {language === 'fr' && 'Cette catégorie est en préparation'}
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Popups */}
      {bannerExperience && <ExperienceDetailPopup experience={bannerExperience} onClose={() => setBannerExperience(null)} />}
      {wizardProduct && (
        <GuidedWizard product={wizardProduct} onComplete={handleWizardComplete} onClose={() => setWizardProduct(null)}
          getImage={getProductImage} index={products.findIndex(p => p.id === wizardProduct.id)} />
      )}
      {selectedProduct && (
        <ProductPopup product={selectedProduct} onClose={() => setSelectedProduct(null)}
          onNavigate={handleProductNav} hasPrev={selectedProduct.index > 0}
          hasNext={selectedProduct.index < products.length - 1}
          totalProducts={products.length} currentIndex={selectedProduct.index}
          onProductAdded={triggerPostAdd} />
      )}
      <AIImageDisclaimer language={language} />
      {upsellData && <UpsellPopup banner={upsellData.banner} upsellProduct={upsellData.upsellProduct} triggerProduct={upsellData.triggerProduct} onAccept={handleUpsellAccept} onDecline={handleUpsellDecline} />}

      {/* Upsell al añadir */}
      {upsellAddProduct && !showDownsell && (
        <UpsellAddPopup
          triggerProduct={upsellAddProduct}
          type="upsell"
          onAccept={() => { setUpsellAddProduct(null); setShowDownsell(false); }}
          onDecline={() => {
            if (upsellAddProduct?.downsell_product_id) {
              setShowDownsell(true);
            } else {
              setUpsellAddProduct(null);
            }
          }}
        />
      )}

      {/* Downsell (si rechaza upsell) */}
      {upsellAddProduct && showDownsell && (
        <UpsellAddPopup
          triggerProduct={upsellAddProduct}
          type="downsell"
          onAccept={() => { setUpsellAddProduct(null); setShowDownsell(false); }}
          onDecline={() => { setUpsellAddProduct(null); setShowDownsell(false); }}
        />
      )}
      {crossSellProduct && <CrossSellStrip triggerProduct={crossSellProduct} onClose={() => setCrossSellProduct(null)} />}
    </div>
  );
};

export default CategoryView;
